/**
 * ExecutorModeControls — small UI primitives for the Playwright / v5 extension
 * executor selector that surfaces on every Play affordance in the dashboard.
 *
 * Two exports:
 *  - <ExecutorPlayButton/>  Compact split-button. Main click runs the default
 *                           ("playwright"); the dropdown caret lets the user
 *                           pick "extension-v5" instead. Designed to slot into
 *                           the existing Play <IconButton> spots in tables.
 *  - <ExecutorModeChip/>    Tiny chip ("Playwright" / "v5 ext") for execution
 *                           history rows + run-detail headers.
 *  - resolveExecutorMode()  Helper that infers the executor from an exec log
 *                           when the backend hasn't filled in `executorMode`
 *                           yet — falls back to 'playwright'.
 *
 * Default mode stays Playwright everywhere — the user has to actively pick
 * "v5 ext" from the menu to opt into the new runner.
 */

import React, { useRef, useState } from 'react';
import {
  ButtonGroup,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  ArrowDropDown as DropDownIcon,
  CloudQueue as PlaywrightIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';

export const EXECUTOR_MODES = [
  {
    value: 'playwright',
    label: 'Run Headless (Playwright)',
    chipLabel: 'Playwright',
    blurb: 'Server-side Playwright — no extension, fastest baseline',
    icon: PlaywrightIcon,
  },
  {
    value: 'extension-v4',
    label: 'Run as Learner (v4 Extension)',
    chipLabel: 'v4 learner',
    blurb: 'AchieveDXP v3 platform + Automatrix runtime — production mirror',
    icon: ExtensionIcon,
  },
];

// Legacy mode — retired from the menus but still rendered as a chip on
// historical run logs so the run-history view doesn't drop a label.
export const LEGACY_EXECUTOR_MODES = [
  {
    value: 'extension-v5',
    label: 'Run via v5 Extension (legacy prototype)',
    chipLabel: 'v5 ext (legacy)',
    blurb: 'Retired prototype — kept for back-compat on old run logs',
    icon: ExtensionIcon,
  },
];

/**
 * Split-button Play affordance. Click the play icon to run with the default
 * executor (Playwright); click the chevron to choose an alternate engine.
 *
 * Props:
 *  - onRun(mode)        called with the chosen 'playwright' | 'extension-v5'
 *  - busy               show a spinner instead of the play icon
 *  - disabled           disable the whole control
 *  - defaultMode        default mode for the main click (default: 'playwright')
 *  - tooltip            tooltip for the main play button (Tooltip wrapped externally is fine too)
 *  - size               'small' | 'medium' (forwarded to IconButton)
 */
export const ExecutorPlayButton = ({
  onRun,
  busy = false,
  disabled = false,
  defaultMode = 'playwright',
  tooltip = 'Run script',
  size = 'small',
  color = 'success',
}) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleSelect = (mode) => {
    setOpen(false);
    onRun?.(mode);
  };

  const stop = (e) => {
    // Defensive: most callers wrap us inside a clickable row.
    e.stopPropagation();
  };

  return (
    <>
      <ButtonGroup
        ref={anchorRef}
        size={size}
        variant="text"
        sx={{ verticalAlign: 'middle' }}
        onClick={stop}
      >
        <Tooltip title={tooltip}>
          <span>
            <IconButton
              size={size}
              color={color}
              onClick={(e) => { stop(e); handleSelect(defaultMode); }}
              disabled={disabled || busy}
            >
              {busy ? <CircularProgress size={size === 'small' ? 16 : 20} /> : <PlayIcon fontSize={size} />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Pick executor mode">
          <span>
            <IconButton
              size={size}
              color={color}
              onClick={(e) => { stop(e); setOpen((v) => !v); }}
              disabled={disabled || busy}
              sx={{ ml: -0.5, px: 0.25 }}
              aria-label="select executor mode"
            >
              <DropDownIcon fontSize={size} />
            </IconButton>
          </span>
        </Tooltip>
      </ButtonGroup>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        onClick={stop}
      >
        {EXECUTOR_MODES.map((m) => {
          const Icon = m.icon;
          return (
            <MenuItem key={m.value} onClick={() => handleSelect(m.value)} dense>
              <ListItemIcon>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={m.label} secondary={m.blurb} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

/**
 * Best-effort: figure out which executor produced an execution log. Reads
 * `executorMode` directly when present, otherwise falls back to a few hints
 * the backend may set, otherwise defaults to 'playwright' (the original
 * runner — that's what every legacy log came from).
 */
export const resolveExecutorMode = (exec) => {
  if (!exec) return 'playwright';
  if (exec.executorMode) return exec.executorMode;
  if (exec.metadata?.executorMode) return exec.metadata.executorMode;
  if (exec.engine) return exec.engine;
  if (exec.metadata?.runner === 'extension-v4') return 'extension-v4';
  if (exec.metadata?.runner === 'extension-v5') return 'extension-v5';
  if (exec.metadata?.extensionVariant === 'v4') return 'extension-v4';
  if (exec.metadata?.extensionVariant === 'v5') return 'extension-v5';
  return 'playwright';
};

/**
 * Tiny chip that labels which executor handled a run. Drop into history rows
 * and run-detail headers next to the status chip.
 */
export const ExecutorModeChip = ({ mode, exec, size = 'small', sx }) => {
  const resolved = mode || resolveExecutorMode(exec);
  const all = [...EXECUTOR_MODES, ...LEGACY_EXECUTOR_MODES];
  const meta = all.find((m) => m.value === resolved) || EXECUTOR_MODES[0];
  const isExtension = resolved === 'extension-v5' || resolved === 'extension-v4';
  // v4 = primary (teal — matches AchieveDXP), v5 = secondary (purple)
  const chipColor = resolved === 'extension-v4'
    ? 'primary'
    : resolved === 'extension-v5'
      ? 'secondary'
      : 'default';
  return (
    <Tooltip title={meta.blurb}>
      <Chip
        size={size}
        label={meta.chipLabel}
        variant={isExtension ? 'filled' : 'outlined'}
        color={chipColor}
        sx={{ height: 20, fontSize: 10, fontWeight: 600, ...sx }}
      />
    </Tooltip>
  );
};

export default ExecutorPlayButton;
